import { Button, Message, Table } from 'fave-ui';
import { PageHeaderWithSpace } from '../../components/common';
import { ColumnsType } from 'antd/lib/table';
import { calcTableHeight, calcTableWidth } from '../../utils/utilFunctions';
import useSetBreadCrumbParams from '../../hooks/useSetBreadCrumbParams';
import { useParams } from 'react-router';
import MonthDropdown from '../../components/common/MonthDropdown';
import YearDropdown from '../../components/common/YearDropdown';
import {
  Invoice,
  useFetchInvoices,
} from '../../services/Invoices/useFetchInvoices';
import { useMemo, useState } from 'react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { usePostInvoicesToEmail } from '../../services/Invoices/usePostInvoicesToEmail';

const tableHeight = calcTableHeight(345);
const tableWidth = calcTableWidth();

const InvoicesView = () => {
  const { id, bank_account_id: bankAccountId } = useParams();
  const hasIds = !!(id && bankAccountId);

  const defaultDate = subMonths(new Date(), 1);
  const [selectedYear, setSelectedYear] = useState(defaultDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    defaultDate.getMonth() + 1,
  );
  const defaultStartDate = format(startOfMonth(defaultDate), 'yyyy-MM-dd');
  const defaultEndDate = format(endOfMonth(defaultDate), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [monthTitle, setMonthTitle] = useState(format(defaultDate, 'MMM'));

  const { data: invoicesData, isFetching: isFetchInvoicesLoading } =
    useFetchInvoices({
      finance_account_id: parseInt(String(bankAccountId)),
      start_date: startDate,
      end_date: endDate,
    });
  const { mutateAsync: postInvoicesToEmail } = usePostInvoicesToEmail();
  const [emailInvoicesLoading, setEmailInvoicesLoading] = useState('');

  const handlePostEmailToInvoices = (invoice_types: string) => {
    setEmailInvoicesLoading(invoice_types);
    postInvoicesToEmail(
      {
        finance_account_id: Number(bankAccountId),
        year: selectedYear,
        month: selectedMonth,
        invoice_types: invoice_types,
      },
      {
        onSuccess: async () =>
          Message.success({
            content:
              'Your statements are being prepared and will be sent to your email.',
          }),
        onSettled: () => setEmailInvoicesLoading(''),
      },
    );
  };

  useSetBreadCrumbParams(
    [
      { label: '', key: String(id) },
      { label: '', key: String(bankAccountId) },
    ],
    hasIds,
  );

  const getParameters = () => {
    const selectedDate = new Date(selectedYear, selectedMonth - 1);
    const firstDay = format(startOfMonth(selectedDate), 'yyyy-MM-dd');
    const lastDay = format(endOfMonth(selectedDate), 'yyyy-MM-dd');
    const month = format(selectedDate, 'MMM');
    return { firstDay, lastDay, month };
  };

  const flatMapInvoices = useMemo(
    () => invoicesData?.pages.flatMap(data => data.invoices),
    [invoicesData],
  );

  const handleApplyFilter = () => {
    const { firstDay, lastDay, month } = getParameters();
    setStartDate(firstDay);
    setEndDate(lastDay);
    setMonthTitle(month);
  };

  const columns: ColumnsType<Invoice> = [
    {
      title: 'Period',
      dataIndex: 'period',
      width: 60,
      render: () => (
        <div>
          {selectedYear} {monthTitle}
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'title',
      width: 80,
      render: (title: string) => <div>{title}</div>,
    },
    {
      title: 'Invoice',
      dataIndex: 'value',
      width: 100,
      render: (value: string) => {
        const isSendEmailLoading = emailInvoicesLoading === value;

        return (
          <Button
            data-id={value}
            key={value}
            type={'primary'}
            onClick={() => handlePostEmailToInvoices(value)}
            loading={isSendEmailLoading}
          >
            Send to me
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeaderWithSpace title={'Invoices'} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <YearDropdown onChange={setSelectedYear} />
        <MonthDropdown onChange={setSelectedMonth} />
        <Button type={'primary'} onClick={handleApplyFilter}>
          Apply filter
        </Button>
      </div>
      <div>
        <Table
          columns={columns}
          dataSource={isFetchInvoicesLoading ? [] : flatMapInvoices}
          scroll={{ x: tableWidth, y: tableHeight }}
          loading={isFetchInvoicesLoading}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default InvoicesView;
